import { useCallback } from 'react';
import { Edge } from '@xyflow/react';
import { Connection } from '@/types/data';
import { MarkerType } from '@xyflow/react';

export const useConnectionManagement = (
  connections: Connection[],
  handleRowsChange: (connections: Connection[]) => void
) => {
  // Function to find a connection in the connections array
  const findConnection = useCallback(
    (source: string, target: string, courier: string) => {
      return connections.findIndex(
        (conn) => conn.start === source && conn.end === target && conn.courier === courier
      );
    },
    [connections]
  );

  // Function to update a connection in the connections array
  const updateConnection = useCallback(
    (source: string, target: string, courier: string, newSource?: string, newTarget?: string) => {
      if (!connections || connections.length === 0) return;

      // Find the connection
      const connIndex = findConnection(source, target, courier);
      if (connIndex === -1) return;

      // Create a new connections array with the updated connection
      const newConnections = [...connections];
      if (newSource) newConnections[connIndex].start = newSource;
      if (newTarget) newConnections[connIndex].end = newTarget;

      // Update the connections using the hook
      handleRowsChange(newConnections);
    },
    [connections, findConnection, handleRowsChange]
  );

  // Function to add a new connection
  const addConnection = useCallback(
    (source: string, target: string, courier: string = 'default') => {
      if (!connections) return;

      // Check if the connection already exists
      const connIndex = findConnection(source, target, courier);
      if (connIndex !== -1) return;

      // Create a new connection
      const newConnection: Connection = {
        courier,
        start: source,
        end: target,
        zone: '',
        priority: '0',
        book_on_scan: 'n',
        book_on_booking: 'n',
        book_on_tracking_from_previous_leg: 'n',
      };

      // Add the new connection to the connections array
      handleRowsChange([...connections, newConnection]);
    },
    [connections, findConnection, handleRowsChange]
  );

  // Function to remove a connection
  const removeConnection = useCallback(
    (source: string, target: string, courier: string) => {
      if (!connections || connections.length === 0) return;

      // Find the connection
      const connIndex = findConnection(source, target, courier);
      if (connIndex === -1) return;

      // Create a new connections array without the removed connection
      const newConnections = [...connections];
      newConnections.splice(connIndex, 1);

      // Update the connections using the hook
      handleRowsChange(newConnections);
    },
    [connections, findConnection, handleRowsChange]
  );

  // Function to create edges from connections
  const createEdgesFromConnections = useCallback(() => {
    if (!connections || connections.length === 0) return [];

    // Keep track of processed bidirectional connections
    const processedBidirectionalPairs = new Set<string>();

    return connections.reduce((edges, conn, index) => {
      // Check if there's a bidirectional connection
      const bidirectional = connections.some(
        (c) => c.start === conn.end && c.end === conn.start && c.courier === conn.courier
      );

      // If this is a bidirectional connection, create a unique identifier for the pair
      if (bidirectional) {
        // Create a unique identifier for this bidirectional pair
        // Sort the locations to ensure the same ID regardless of direction
        const locations = [conn.start, conn.end].sort();
        const pairId = `${locations[0]}-${locations[1]}-${conn.courier}`;

        // If we've already processed this bidirectional pair, skip creating a duplicate edge
        if (processedBidirectionalPairs.has(pairId)) {
          return edges;
        }

        // Mark this pair as processed
        processedBidirectionalPairs.add(pairId);
      }

      edges.push({
        id: `e${index}`,
        source: conn.start,
        target: conn.end,
        label: `${conn.courier} ${conn.priority}`,
        type: 'default',
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        // If bidirectional, also add a start marker
        ...(bidirectional && {
          markerStart: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
          },
        }),
        style: { strokeWidth: 2 },
        data: { connection: conn, bidirectional },
      });

      return edges;
    }, [] as Edge[]);
  }, [connections]);

  return {
    findConnection,
    updateConnection,
    addConnection,
    removeConnection,
    createEdgesFromConnections,
  };
};
